import type { ForeignEntityId } from "../handles/foreignEntityId";

export const ExpressionOperators = ["==", "!=", ">", "<", ">=", "<=", "and", "or"] as const;
export type ExpressionOperator = (typeof ExpressionOperators)[number];

export type ConnectiveOperator = Extract<ExpressionOperator, "and" | "or">;
export type ConditionOperator = Extract<ExpressionOperator, "==" | "!=" | ">" | "<" | ">=" | "<=" | "contains">;

export const EqualityOperators: readonly ConditionOperator[] = ["==", "!="];
export const ComparisonOperators: readonly ConditionOperator[] = [">", "<", ">=", "<=", ...EqualityOperators];

export const ConnectiveOperators: readonly ConnectiveOperator[] = ["and", "or"];

export const operatorRegex = /\s(==|!=|>|<|>=|<=)\s/;

export type ConditionsBuilderTypeDefType = "OPTION_SET" | "PARAMETER";

/**
  connectiveRegex is designed to split a formula by "and" / "or" connectives that are not, themselves,
  included within an optionSetId. It will also preserve escaped string values
  i.e. the formula 'param_8VkV22DI8ToriHxk == "8\" CMU" and Orion == "6\" CMU"' would be split like so:
  [ 'param_8VkV22DI8ToriHxk == "8" CMU"', 'and', 'Orion == "6" CMU"' ]
 */
export const connectiveRegex = /(?!"[\\"]*)\b(and|or)\b(?![\w\s]*[\\"]*")/g;

export const operatorPrettyNames: {
  [key in ExpressionOperator]: string;
} = {
  and: "&",
  or: "or",
  "==": "=",
  "!=": "≠",
  ">": ">",
  "<": "<",
  ">=": "≥",
  "<=": "≤",
} as const;

export const depressedOperators: ExpressionOperator[] = ["!=", "or"];

export type ConditionTypeDefId =
  | string
  | ForeignEntityId<"OPTION_SET">
  | ForeignEntityId<"LOCAL_PARAMETER">
  | ForeignEntityId<"GLOBAL_PARAMETER">
  | ForeignEntityId<"SYSTEM_PARAMETER">
  | ForeignEntityId<"LABEL">;

export interface Condition {
  readonly typeDefId: ConditionTypeDefId; // LHS
  readonly operator: ConditionOperator;
  readonly value: string | ForeignEntityId<"OPTION"> | number | boolean | undefined | null; // RHS

  readonly connective?: ConnectiveOperator;
  readonly groupId?: string;
}

export interface ConditionGroup {
  readonly id: string;
  readonly conditions: Condition[];
  readonly connective: ConnectiveOperator;
}
